import { WithContext, BusinessEvent } from 'schema-dts';

import { Conference, ConferenceType } from 'types/models/Conference';
import ContactConfig from 'types/ContactConfig';
import { getPlainTextContent } from '@templates/Description';
import getFullStreetAddress from 'utils/getFullStreetAddress';
import resolveImageUrl from 'utils/resolveImageUrl';
import map from 'utils/map';

type Data = Pick<
  Conference,
  'abstract' | 'speakers' | 'type' | 'location' | 'dateFrom' | 'dateTo' | 'image' | 'seoData' | 'tags'
  // | 'maxSlots'
  // | 'usedSlots'
>;

const typeMap: Record<ConferenceType['id'], BusinessEvent['eventAttendanceMode']> = {
  STATIONARY: 'OfflineEventAttendanceMode',
  REMOTE: 'OnlineEventAttendanceMode',
};

export const getConferenceStructuredData = (
  { abstract, type, speakers, location, dateFrom, dateTo, image, seoData, tags }: Data,
  { appUrl, contact, companyLogoUrl }: { appUrl: string; contact: ContactConfig; companyLogoUrl?: string }
): WithContext<BusinessEvent> => {
  const plainTextDescription = getPlainTextContent(seoData?.description);
  const imageUrl = resolveImageUrl(
    {
      preset: 'og',
      extension: image.extension,
      name: image.basename,
      description: 'cover',
      productName: seoData?.header,
    },
    { appUrl }
  );
  const structuredData: WithContext<BusinessEvent> = {
    '@context': 'https://schema.org',
    '@type': 'BusinessEvent',
    about: abstract || undefined,
    startDate: dateFrom,
    endDate: dateTo,
    eventAttendanceMode: typeMap[type.id],
    eventStatus: 'EventScheduled',
    inLanguage: 'pl-PL',
    isAccessibleForFree: true,
    location:
      type.id === 'STATIONARY' && location
        ? {
            '@type': 'PostalAddress',
            addressCountry: 'PL',
            addressLocality: location.address.locality,
            postalCode: location.address.postalCode,
            streetAddress: getFullStreetAddress(location.address),
          }
        : undefined,
    // maximumAttendeeCapacity: maxSlots,
    // remainingAttendeeCapacity: maxSlots - usedSlots,
    name: seoData?.header,
    description: plainTextDescription,
    image: imageUrl,
    keywords: map(tags, 'tag.name'),
    organizer: {
      '@type': 'Organization',
      name: contact.name,
      url: contact.companyUrl,
      logo: companyLogoUrl,
    },
    performers: speakers.map(({ speaker }) => ({
      '@type': 'Person',
      name: speaker.contact.address.fullName,
      description: speaker.description,
      jobTitle: speaker.position,
      sameAs: speaker.website,
    })),
  };
  return structuredData;
};
